import { useEffect } from 'react';
import BaseContainer from 'app/containers/BaseContainer';
import { MainMenu } from './MainMenu';
import { RouteComponentProps } from 'react-router-dom';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router';
import { BerichtePanel } from 'app/components/berichte/BerichtePanel';
import { BerichteTreeMenu } from 'app/components/berichte/BerichteTreeMenu';
import { BaseTreeStore, AgtBaseTreeStore } from 'app/stores/core/base.tree.store';
import { ArrayParam, StringParam, useQueryParam } from 'use-query-params';
import { runInAction } from 'mobx';

export const BerichteNavBar = observer(() => {
	// needed to avoid path loop when switching from berichte to agenturen
	const { agenturListUiStore } = useStore();
	useEffect(() => {
		return () => {
			agenturListUiStore.currentFilter.selectSingleAgtId(undefined);
		};
	});
	return <div>Berichte</div>;
});
const BerichteContainer = (props: RouteComponentProps) => {
	const { berichteUiiStore } = useStore();

	return (
		<BaseContainer sideMenu={<MainMenu {...props} />} navbar={<BerichteNavBar />}>
			<TreePathLoader store={berichteUiiStore} />
			<AgtQueryStringSetter />
			<BerichteTreeMenu />
			<BerichtePanel />
		</BaseContainer>
	);
};

export interface IAgtFilterParms {
	agtId?: string;
	betreuerBnrId?: string;
	segment?: string;
	schwerpunkt?: string;
	fullText?: string;
}

export interface BerichteParamTypes {
	level1?: string;
	level2?: string;
	level3?: string;
	level4?: string;
	level5?: string;
}

interface ILoader {
	store: AgtBaseTreeStore;
}
interface IBaseLoader {
	store: BaseTreeStore;
}

export const QueryParmDef = {
	agtId: ArrayParam,
	betreuerBnrId: StringParam,
	schwerpunkt: StringParam,
	segment: StringParam,
	fullText: StringParam,
	gs: StringParam,
	vd: StringParam,
	unbetreut: StringParam,
};
export const BaseTreePathLoader = observer((props: IBaseLoader) => {
	const { agenturListUiStore } = useStore();
	const treeStore = props.store;
	const { level1, level2, level3, level4, level5 } = useParams<BerichteParamTypes>();
	treeStore.isInAgt = false;
	useEffect(() => {
		// if (agenturListUiStore.currentFilter.agtId && agenturListUiStore.currentFilter.agtId.length === 1) {
		// 	treeStore.setSelectedAgtById(agenturListUiStore.currentFilter.agtId[0]);
		// } else {
		// 	treeStore.setSelectedAgtById(undefined);
		// }
		// treeStore.setSelectBnrById(agenturListUiStore.currentFilter.betreuerBnrId);
		treeStore.menu.setActivePath(level1, level2, level3, level4, level5);
		treeStore.menu.openByPath(level1, level2, level3, level4, level5);
	}, [treeStore, level1, level2, level3, level4, level5, agenturListUiStore.currentFilter.singleSelectedAgtId, agenturListUiStore.currentFilter.singleSelectedBetreuerId]);

	return <></>;
});

export const TreePathLoader = observer((props: ILoader) => {
	const { agenturListUiStore, agtAkumaAktionenUiStore } = useStore();
	const treeStore = props.store;
	const { level1, level2, level3, level4, level5 } = useParams<BerichteParamTypes>();
	treeStore.isInAgt = false;
	useEffect(() => {
		if (agenturListUiStore.currentFilter.singleSelectedAgtId) {
			treeStore.setSelectedAgtById(agenturListUiStore.currentFilter.singleSelectedAgtId);
		} else {
			treeStore.setSelectedAgtById(undefined);
		}
		treeStore.setSelectBnrById(agenturListUiStore.currentFilter.singleSelectedBetreuerId);
		treeStore.menu.setActivePath(level1, level2, level3, level4, level5);
		treeStore.menu.openByPath(level1, level2, level3, level4, level5);
	}, [treeStore, level1, level2, level3, level4, level5, agenturListUiStore.currentFilter.singleSelectedAgtId, agenturListUiStore.currentFilter.singleSelectedBetreuerId]);

	// Set VM context based on current node's vmType
	useEffect(() => {
		const currentNode = treeStore.menu.current;
		if (currentNode?.vmType) {
			// Get items based on vmType
			let items: any[] = [];
			switch (currentNode.vmType) {
				case 'AkumaAktionenVM':
					items = agtAkumaAktionenUiStore.items;
					break;
				// Add other VM types here as needed
			}

			if (items.length > 0) {
				agenturListUiStore.currentFilter.setVmContext(currentNode.vmType, items);
			}
		}
	}, [treeStore.menu.current, agtAkumaAktionenUiStore.items, agenturListUiStore.currentFilter]);

	return <></>;
});

export const AgtQueryStringSetter = observer(() => {
	const { agenturListUiStore, uiStore } = useStore();
	const history = useHistory();

	// Use fixed hooks for known filter parameters to avoid hook count issues
	const [agtIdQuery] = useQueryParam('agtId', StringParam);
	const [betreuerBnrIdQuery] = useQueryParam('betreuerBnrId', StringParam);
	const [segmentQuery] = useQueryParam('segment', StringParam);
	const [schwerpunktQuery] = useQueryParam('schwerpunkt', StringParam);
	const [fullTextQuery] = useQueryParam('fullText', StringParam);
	const [gsQuery] = useQueryParam('gs', StringParam);
	const [vdQuery] = useQueryParam('vd', StringParam);
	const [steartQuery] = useQueryParam('steart', StringParam);
	const [jahresthemenQuery] = useQueryParam('jahresthemen', StringParam);
	const [vertkatsQuery] = useQueryParam('vertkat', StringParam);
	const [gpQuery] = useQueryParam('gp', StringParam);
	const [unbetreutQuery] = useQueryParam('unbetreut', StringParam);
	const [agtTypQuery] = useQueryParam('agtTyp', StringParam);
	const [focusthemenQuery] = useQueryParam('focusthemen', StringParam);
	const [orgainvalidQuery] = useQueryParam('orgainvalid', StringParam);

	// VM-specific filters - use dynamic handling
	const [campaignIdQuery] = useQueryParam('campaignId', StringParam);
	const [campaignNameQuery] = useQueryParam('campaignName', StringParam);
	const [topCampaignQuery] = useQueryParam('topCampaign', StringParam);
	const [topCampaignsQuery] = useQueryParam('topCampaigns', StringParam);
	const [focusCampaignQuery] = useQueryParam('focusCampaign', StringParam);
	const [sparteQuery] = useQueryParam('sparte', StringParam);
	const [beratungsartQuery] = useQueryParam('beratungsart', StringParam);
	const [minCampaignsQuery] = useQueryParam('minCampaigns', StringParam);
	const [minBearbeitungsquoteQuery] = useQueryParam('minBearbeitungsquote', StringParam);

	// Non-filter query params (for UI state, not filtering)
	const [selectedCampaignIdQuery] = useQueryParam('selectedCampaignId', StringParam);
	const [showMap] = useQueryParam('showMap', StringParam);

	// Map query parameters to filter setters
	const queryParamMap: Record<string, string | null | undefined> = {
		agtId: agtIdQuery,
		betreuerBnrId: betreuerBnrIdQuery,
		segment: segmentQuery,
		schwerpunkt: schwerpunktQuery,
		fullText: fullTextQuery,
		gs: gsQuery,
		vd: vdQuery,
		steart: steartQuery,
		jahresthemen: jahresthemenQuery,
		vertkat: vertkatsQuery,
		gp: gpQuery,
		unbetreut: unbetreutQuery,
		agtTyp: agtTypQuery,
		focusthemen: focusthemenQuery,
		orgainvalid: orgainvalidQuery,
		campaignId: campaignIdQuery,
		campaignName: campaignNameQuery,
		topCampaign: topCampaignQuery,
		topCampaigns: topCampaignsQuery,
		focusCampaign: focusCampaignQuery,
		sparte: sparteQuery,
		beratungsart: beratungsartQuery,
		minCampaigns: minCampaignsQuery,
		minBearbeitungsquote: minBearbeitungsquoteQuery,
	};

	// Initialize filters from query parameters
	useEffect(() => {
		// console.log('[DEBUG] BerichteContainer useEffect triggered');

		// Skip if we're currently in a filter update cycle to avoid clearing just-restored filters
		if (agenturListUiStore.filterHasChanged === 'changing') {
			// console.log('[DEBUG] Skipping filter processing - in filter update cycle');
			return;
		}

		// First, handle available filters (excluding VM filters which are handled separately)
		agenturListUiStore.currentFilter.filters
			.filter((f) => !f.vmType)
			.forEach((f) => {
				const queryValue = queryParamMap[f.name];
				if (queryValue !== undefined) {
					console.log(`[DEBUG] Setting regular filter ${f.name} = ${queryValue}`);
					f.setValueFromQueryString(queryValue);
				} else {
					// Clear filter if not present in query string
					// console.log(`[DEBUG] Clearing regular filter ${f.name} (not in query string)`);
					f.setValueFromQueryString(null);
				}
			});

		// Store VM-specific query values for later restoration when VM context is available
		const vmFilterNames = ['campaignId', 'campaignName', 'topCampaign', 'topCampaigns', 'focusCampaign', 'sparte', 'beratungsart', 'minCampaigns', 'minBearbeitungsquote'];
		// console.log('[DEBUG] Processing VM filter names');
		vmFilterNames.forEach((filterName) => {
			const queryValue = queryParamMap[filterName];
			// console.log(`[DEBUG] Checking ${filterName}: ${queryValue}`);
			if (queryValue !== undefined) {
				// Check if this VM filter already has a value (was restored)
				const existingFilter = agenturListUiStore.currentFilter.filters.find((f) => f.name === filterName);
				if (existingFilter && existingFilter.hasValue) {
					// console.log(`[DEBUG] Filter ${filterName} already has value, not overwriting`);
					return;
				}
				// console.log(`[DEBUG] Storing ${filterName} = ${queryValue}`);
				agenturListUiStore.currentFilter.storeVmFilterValue(filterName, queryValue);
			} else {
				// Clear VM filter if not present in query string
				// But only if it's not pending restoration AND doesn't currently have a value
				const hasPendingValue = agenturListUiStore.currentFilter.pendingVmFilterValues.has(filterName);
				const existingFilter = agenturListUiStore.currentFilter.filters.find((f) => f.name === filterName);
				const hasCurrentValue = existingFilter && existingFilter.hasValue;

				// console.log(`[DEBUG] VM filter ${filterName} state: pending=${hasPendingValue}, exists=${!!existingFilter}, hasValue=${hasCurrentValue}, value=${existingFilter?.value}`);

				if (hasPendingValue) {
					// console.log(`[DEBUG] Skipping clear of VM filter ${filterName} - pending restoration`);
				} else if (hasCurrentValue) {
					// console.log(`[DEBUG] Skipping clear of VM filter ${filterName} - currently has value`);
				} else {
					// console.log(`[DEBUG] Clearing VM filter ${filterName} (not in query string)`);
					if (existingFilter) {
						// console.log(`[DEBUG] Clearing existing filter ${filterName}`);
						existingFilter.setValueFromQueryString(null);
					}
				}
			}
		});
	}, [agenturListUiStore.currentFilter, ...Object.values(queryParamMap)]);

	useEffect(() => {
		runInAction(() => {
			uiStore.showMap = showMap === '1';
		});
	}, [showMap]);

	// Update URL when filters change
	useEffect(() => {
		const params = new URLSearchParams(history.location.search);
		const queryParamsArray = Array.from(new URLSearchParams(agenturListUiStore.currentFilter.asQuery));
		// console.log(`[DEBUG] URL Update - asQuery:`, agenturListUiStore.currentFilter.asQuery);
		// console.log(`[DEBUG] URL Update - filters with values:`, agenturListUiStore.currentFilter.filtersWithValue.map(f => `${f.name}=${f.value}`));
		agenturListUiStore.currentFilter._filters.forEach((f) => {
			params.delete(f.name);
		});
		queryParamsArray.forEach(([key, value]) => {
			params.set(key, value);
		});
		if (uiStore.showMap) {
			params.set('showMap', '1');
		}
		// console.log(`[DEBUG] Final URL params:`, params.toString());
		history.replace({ search: params.toString() });
	}, [agenturListUiStore.filterHasChanged, agenturListUiStore, history, uiStore.showMap]);

	return <></>;
});

export default BerichteContainer;
